import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Descriptions } from 'antd';
import { useStore } from '@hub/helpers/use-store';
import { formatDate } from '@monorepo/client-common';
import { Card } from '@monorepo/react-components';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import {
  AccountStats as AccountStatsType,
  UnavailableValue,
} from '@monorepo/types';

interface Props {
  accountId: string;
}

const AccountStats: FunctionComponent<Props> = ({ accountId }) => {
  const {
    dataStore: { accountStore },
  } = useStore();
  const { loadingState, updateLoadingState } = useLoading();
  const [accountStats, setAccountStats] = useState<AccountStatsType>({
    riskRewardRatio: 0,
    successRate: 0,
  });
  const { t } = useTranslation(['dashboardTemplates']);

  useEffect(() => {
    const init = async () => {
      try {
        updateLoadingState(LoadingState.Loading);
        const result = await accountStore.getStats(accountId);

        setAccountStats(result);
      } catch (e) {
        console.error(`Failed getting overview`, e);
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    init();
  }, [accountId]);

  const isLoading = loadingState === LoadingState.Loading;

  return (
    <Card className="metric-card" loading={isLoading} title={`Account Stats`}>
      <Descriptions column={1} size="small">
        <Descriptions.Item label={t('dashboardTemplates:best_stock')}>
          {accountStats.bestStock?.symbol || UnavailableValue.Dash}
        </Descriptions.Item>
        <Descriptions.Item label={t('dashboardTemplates:best_day')}>
          {accountStats.bestDay
            ? formatDate(accountStats.bestDay?.day)
            : UnavailableValue.Dash}
        </Descriptions.Item>
        <Descriptions.Item label={t('dashboardTemplates:risk_reward_ratio')}>
          {accountStats.riskRewardRatio
            ? `1:${accountStats.riskRewardRatio}`
            : UnavailableValue.Dash}
        </Descriptions.Item>
        <Descriptions.Item label={t('dashboardTemplates:success_rate')}>
          {accountStats.successRate
            ? `${accountStats.successRate.toFixed(0)}%`
            : UnavailableValue.Dash}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

export default AccountStats;
